import Avatar from '@app/src/components/Ui/Avatar'
import type { CreditInfo } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/ApplyToSection'
import ApplyToSection from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreationSteps/ApplyToSection'
import { Divider, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface TriggerActionApplyToSummaryProps {
  triggerIcon: React.ReactNode
  triggerPrimaryId: string
  triggerExtra?: React.ReactNode
  actionIcon: React.ReactNode
  actionPrimaryId: string
  actionSecondaryId?: string
  actionExtra?: React.ReactNode
  bannerMessageId: string
  affectedCount: number | undefined
  isLoadingCount: boolean
  creditInfo?: CreditInfo
  isAiAutomation?: boolean
}

const TriggerActionApplyToSummary: React.FC<TriggerActionApplyToSummaryProps> = ({
  triggerIcon,
  triggerPrimaryId,
  triggerExtra,
  actionIcon,
  actionPrimaryId,
  actionSecondaryId,
  actionExtra,
  bannerMessageId,
  affectedCount,
  isLoadingCount,
  creditInfo,
  isAiAutomation = false,
}) => {
  const { formatMessage } = useIntl()

  return (
    <Stack spacing={3} pb={3}>
      <Stack>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'form.createAutomation.summary.trigger' })}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {formatMessage({ id: 'form.createAutomation.summary.whenThisHappens' })}
        </Typography>
        <ListItem disablePadding sx={{ pt: 2 }}>
          <ListItemIcon>
            <Avatar variant="circular" sx={{ bgcolor: 'success.light', color: 'secondary.light' }}>
              {triggerIcon}
            </Avatar>
          </ListItemIcon>
          <ListItemText primary={formatMessage({ id: triggerPrimaryId })} />
        </ListItem>
        {triggerExtra}
      </Stack>

      <Divider />

      <Stack>
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMessage({ id: 'form.createAutomation.summary.action' })}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {formatMessage({ id: 'form.createAutomation.summary.doThis' })}
        </Typography>
        <ListItem disablePadding sx={{ pt: 1 }}>
          <ListItemIcon>
            <Avatar
              variant="circular"
              sx={{
                bgcolor: isAiAutomation ? 'communication.light' : 'success.light',
                color: isAiAutomation ? 'communication.dark' : 'secondary.light',
              }}
            >
              {actionIcon}
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={formatMessage({ id: actionPrimaryId })}
            secondary={actionSecondaryId ? formatMessage({ id: actionSecondaryId }) : undefined}
          />
        </ListItem>
        {actionExtra}
      </Stack>

      <Divider />

      <ApplyToSection
        bannerMessageId={bannerMessageId}
        affectedCount={affectedCount}
        isLoadingCount={isLoadingCount}
        creditInfo={creditInfo}
      />
    </Stack>
  )
}

export default TriggerActionApplyToSummary
